import {Company, EmbeddedBlock, EmbeddedBlockVariant} from '@tinct/core-api-models';
import {LogosBanner, type LogosBannerLogo} from '@tinct/web-components';
import {buyerLogoVariantKey} from '@tinct/web-feature-companies';
import {
  applyLogoTemplateInPlace,
  loadNaturalDimensions,
  logosForCarousel,
  parseLogoTemplate,
  type NaturalDims,
} from '@tinct/tinct-sdk/logo-template';
import {type BlockVariantRenderer} from './types';

/**
 * The base logo used for fill-color (silhouette) rendering: the fill repaints
 * every pixel, so any variant works and the first resolvable one wins,
 * brandmark variants before full logos. Mirrors `getBuyerFillLogoUrl`.
 */
const FILL_LOGO_KEYS = [
  'brandmarkLogo',
  'brandmarkLogoGreyscale',
  'brandmarkLogoDark',
  'brandmarkLogoGreyscaleDark',
] as const;

/**
 * A banner logo plus the natural pixel dimensions of its source image (when
 * the API resolves them), used by the template engine to size logo slots.
 * Structurally the SDK's `VariantLogo`.
 */
type VariantLogo = LogosBannerLogo & {
  width?: number | null;
  height?: number | null;
};

/**
 * Extracts the ordered company logo URLs the banner needs from the variant's
 * companies; drops companies without a resolvable logo URL. With a fill color
 * any base variant works; otherwise the block's greyscale/dark flags pick the
 * brandmark variant, and the plain `logo` / `logoGreyscale` fallbacks only
 * exist for the non-dark combinations.
 */
function getVariantLogos(variant: EmbeddedBlockVariant, block: EmbeddedBlock): VariantLogo[] {
  const fillColor = block.logoFillColor ?? null;
  const key = buyerLogoVariantKey({
    greyscale: block.greyscale ?? false,
    dark: block.dark ?? false,
  });
  return (variant.companies ?? [])
    .map((company): VariantLogo | null => {
      let image: Company['logo'] | undefined;
      if (fillColor) {
        image = FILL_LOGO_KEYS.map((k) => company[k]).find((i) => i?.url);
      } else {
        const fallback = block.dark
          ? null
          : block.greyscale
            ? company.logoGreyscale
            : company.logo;
        image = company[key]?.url ? company[key] : fallback;
      }

      return image?.url
        ? {
            src: image.url,
            alt: company.name ?? '',
            width: image.originalWidth,
            height: image.originalHeight,
          }
        : null;
    })
    .filter((logo): logo is VariantLogo => logo !== null);
}

// Natural-dimension cache for logos the API payload carries no dimensions
// for, mirroring the SDK's enrichment: each src is probed at most once and
// the result is kept for every later render pass.
const naturalDims = new Map<string, NaturalDims | null>();
const naturalDimsRequested = new Set<string>();

/**
 * Returns the logos enriched with every natural dimension known so far — from
 * the payload when present, else from a completed probe. Missing dimensions
 * are loaded once per src; `onLoaded` fires when a batch settles so the caller
 * can re-render with the refined widths. Rendering never waits: a logo whose
 * ratio is still unknown is applied with the engine's fallback sizing.
 */
function enrichLogoDims(logos: VariantLogo[], onLoaded: () => void): VariantLogo[] {
  const missing = logos.filter((logo) => !logo.width || !logo.height);
  if (missing.length === 0) return logos;

  const toLoad = missing
    .map((logo) => logo.src)
    .filter((src) => !naturalDimsRequested.has(src));
  toLoad.forEach((src) => naturalDimsRequested.add(src));
  if (toLoad.length > 0) {
    loadNaturalDimensions(toLoad).then((loaded) => {
      loaded.forEach((value, src) => naturalDims.set(src, value));
      onLoaded();
    });
  }

  return logos.map((logo) => {
    if (logo.width && logo.height) return logo;
    const natural = naturalDims.get(logo.src);
    return natural ? { ...logo, ...natural } : logo;
  });
}

/**
 * Renders a `customer_logo` variant. Blocks with a `logoCount` carry a
 * template variant value (`LOGO_URL` placeholders) applied by the SDK's
 * template engine (`applyLogoTemplateInPlace`) — the same in-place mutation,
 * slot pairing, and calibration-based sizing the edge runs on customer pages,
 * so the preview matches production. Blocks identified before templating
 * existed (`logoCount` null) fall back to the `<LogosBanner>` React component
 * (portaled into the iframe).
 *
 * With no company logo to show (empty selection, or none resolvable for the
 * block's theme) the element is skipped: the page's own logos stay, never
 * hidden, and never the default renderer — a template `value` is placeholder
 * markup. Mirrors the SDK's `applyBlocks`.
 */
export const customerLogoRenderer: BlockVariantRenderer = {
  type: 'customer_logo',
  render(variant, block) {
    const fillColor = block.logoFillColor ?? null;
    const logos = getVariantLogos(variant, block);

    if (logos.length === 0) return { kind: 'skip' };

    if (block.logoCount != null) {
      const template = parseLogoTemplate(variant.value);
      if (!template) return null;

      const slotLogos = logos.slice(0, template.uniqueSlotCount);
      return {
        kind: 'in-place',
        // The n-th matched row of a grouped block takes its own share of the
        // companies (desktop marquee first, then the mobile one).
        apply: (element, requestReapply, index) => {
          applyLogoTemplateInPlace(
            element,
            template,
            enrichLogoDims(logosForCarousel(template, slotLogos, index), requestReapply),
            fillColor,
            { onRefine: requestReapply, carouselIndex: index }
          );
        },
      };
    }

    return {
      kind: 'react',
      node: (
        <LogosBanner
          logos={logos}
          fillColor={fillColor}
          className={'w-full'}
          size={block.largeLogo ? 'large' : 'default'}
        />
      ),
      signature: [...(fillColor ? [fillColor] : []), ...logos.map((logo) => logo.src)].join('|'),
      elementStyle: {
        width: '100%',
      }
    };
  },
};
